<script setup>
import { useRouter } from 'vue-router'
import { useCookie } from '@core/composable/useCookie'
import { useAbility } from '@casl/vue'
import { useI18n } from 'vue-i18n'
import { onMounted, watch, ref, computed } from 'vue'

const router = useRouter()
const ability = useAbility()
const minimalUserData = useCookie('userData')
const { t } = useI18n()

// Get full user data from localStorage
const fullUserData = ref(null)
const userData = computed(() => fullUserData.value || minimalUserData.value)

// Load full user data from localStorage
const loadFullUserData = () => {
  try {
    const stored = localStorage.getItem('fullUserData')
    if (stored) {
      fullUserData.value = JSON.parse(stored)
    }
  } catch (error) {
    console.warn('Failed to load full user data:', error)
  }
}

// Load data on mount
onMounted(() => {
  loadFullUserData()
})


// Logout function
const logout = async () => {
  // Remove "accessToken" from cookie
  useCookie('accessToken').value = null

  // Remove "userData" from cookie
  minimalUserData.value = null

  // Clear localStorage data
  localStorage.removeItem('fullUserData')
  localStorage.removeItem('userAbilityRules')
  
  // Clear local state
  fullUserData.value = null

  // Reset ability to initial ability
  ability.update([])
  
  // Redirect to login page
  await router.push('/login')
}

// Navigate functions
const goToProfile = () => {
  router.push({ name: 'user-profile-tab', params: { tab: 'account' } })
}

const goToSettings = () => {
  router.push({ name: 'account-settings-tab', params: { tab: 'account' } })
}
</script>

<template>
  <VBadge
    v-if="userData"
    dot
    bordered
    location="bottom right"
    offset-x="1"
    offset-y="2"
    color="success"
  >
    <VAvatar
      size="38"
      class="cursor-pointer"
    >
      <template v-if="userData && userData.avatar">
        <VImg
          v-if="typeof userData.avatar === 'string' && userData.avatar.startsWith('http')"
          :src="userData.avatar"
        />
        <VImg
          v-else-if="typeof userData.avatar === 'string'"
          :src="`${window.location.origin}${userData.avatar.startsWith('/') ? '' : '/'}${userData.avatar}`"
        />
        <span v-else>{{ JSON.stringify(userData.avatar) }}</span>
      </template>
      <VImg
        v-else
        src="/images/avatars/default.png"
      />

      <!-- User Menu -->
      <VMenu
        activator="parent"
        width="240"
        location="bottom end"
        offset="12px"
      >
        <VList>
          <!-- User Header -->
          <div class="py-2 px-3">
            <div class="d-flex gap-2 align-center">
              <VListItemAction>
                <VBadge
                  dot
                  location="bottom right"
                  offset-x="3"
                  offset-y="3"
                  color="success"
                  bordered
                >
                  <VAvatar>
                    <template v-if="userData && userData.avatar">
                      <VImg
                        v-if="typeof userData.avatar === 'string' && userData.avatar.startsWith('http')"
                        :src="userData.avatar"
                      />
                      <VImg
                        v-else-if="typeof userData.avatar === 'string'"
                        :src="`${window.location.origin}${userData.avatar.startsWith('/') ? '' : '/'}${userData.avatar}`"
                      />
                      <span v-else>{{ JSON.stringify(userData.avatar) }}</span>
                    </template>
                    <VImg
                      v-else
                      src="/images/avatars/default.png"
                    />
                  </VAvatar>
                </VBadge>
              </VListItemAction>

              <div>
                <VListItemSubtitle class="text-body-2 text-medium-emphasis">
                  {{ userData.profile?.first_name }} {{ userData.profile?.last_name }}
                </VListItemSubtitle>
                <VListItemSubtitle class="text-capitalize text-disabled text-body-2">
                  {{ userData.role }}
                </VListItemSubtitle>
              </div>
            </div>
          </div>

          <VDivider />
          
          <!-- Profile Link -->
          <VListItem @click="goToProfile">
            <template #prepend>
              <VIcon
                icon="tabler-user"
                size="22"
              />
            </template>
            <VListItemTitle>{{ t('user.profile') }}</VListItemTitle>
          </VListItem>
          
          <!-- Settings Link -->
          <VListItem @click="goToSettings">
            <template #prepend>
              <VIcon
                icon="tabler-settings"
                size="22"
              />
            </template>
            <VListItemTitle>{{ t('settings.title') }}</VListItemTitle>
          </VListItem>
          
          <VDivider />
          
          <!-- Logout Button -->
          <div class="px-4 py-3">
            <VBtn
              block
              color="error"
              variant="outlined"
              :prepend-icon="'tabler-logout'"
              @click="logout"
            >
              {{ t('auth.logout') }}
            </VBtn>
          </div>
        </VList>
      </VMenu>
    </VAvatar>
  </VBadge>
</template>
